import React, { useEffect, useMemo, useState } from 'react';
import { Autocomplete, Box, FormControl, MenuItem, MenuList, Paper, TextField } from '@mui/material';
import { useAppDispatch } from '../../hooks/useAppDispatch';
import { useAppSelector } from '../../hooks/useAppSelector';
import { useDebounce } from '../../hooks/useDebounce';
import { IGeoData } from '../../models/service/service';
import { searchPlaces } from '../../store/actions/actions';
import { organizerSlice } from '../../store/slice/slice';
import { errorNames, errorRegExp } from '../../utils/constants';
import { convertWeekdaysReverse } from '../../utils/data';
import { ErrorText } from '../errorText/ErrorText';
import { DateForm } from '../forms/dateForm/DateForm';
import { PhoneInput } from '../phoneInput';
import { borderInput, formWrapper, infoForm, useStyles } from './formAddress.styles';
import { IProps } from './types';

export const FormAddress: React.FC<IProps> = (props) => {
  const { control, register, required, errors, opportunities, setValue, edit, setDays, submitted } = props;
  const classes = useStyles();
  const [address, setAddress] = useState('');
  const [clickAddress, setClickAddress] = useState(false);
  const debouncedSearchAddress = useDebounce(address, 200);
  const addresses = useAppSelector((state) => state.organizerReducer.addresses) as string[];
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (debouncedSearchAddress) {
      searchPlaces(debouncedSearchAddress)(dispatch);
    }
  }, [debouncedSearchAddress]);

  useEffect(() => {
    if (edit && opportunities) {
      setAddress(opportunities.address);
      setValue('name', opportunities.name);
      setValue('address', opportunities.address);
      setValue('contactName', opportunities.contactName);
      setValue('contactEmail', opportunities.contactEmail);
      setValue('endTime', opportunities.endDate);
      setValue('startTime', opportunities.startDate);
      setValue('contactPhone', opportunities.contactPhone || '');
      setValue('firstOccurrence', opportunities.startDate);
      setValue('maximumVolunteers', opportunities.maximumVolunteers);
    }
    if (edit && opportunities?.frequency) {
      setDays(convertWeekdaysReverse[opportunities.frequency[0]]);
    }
  }, [opportunities]);

  const handleChangeAddress = (value: string) => {
    setClickAddress(true);
    setAddress(value);
  };

  const handleClickAddress = (value: string) => {
    setValue('address', value);
    setAddress(value);
    dispatch(organizerSlice.actions.setAddresses([]));
    setClickAddress(false);
  };

  const [addressFormControls, onAddressChange] = useMemo(() => {
    const { onChange, ...addressControls } = register('address', {
      required,
      validate: () => !clickAddress,
    });

    const handleAddressChange = async(event: React.ChangeEvent<HTMLInputElement>) => {
      const task = onChange(event);
      handleChangeAddress(event.target.value);
      await onChange(event);

      return task;
    };

    return [addressControls, handleAddressChange];
  }, [register, required]);

  return (
    <Box sx={infoForm}>
      <FormControl>
        <TextField
          id="outlined-basic"
          label="Opportunity name*"
          fullWidth={true}
          {...register('name', {
            required,
            maxLength: 100,
          })}
          error={!!errors?.name}
          sx={borderInput}
        />
        {errors?.name?.type === 'required' && <ErrorText text={errorNames.opportunityNameRequired} />}
        {errors?.name?.type === 'maxLength' && <ErrorText text={errorNames.opportunityNameLength} />}
      </FormControl>
      <DateForm {...props} />
      <Box sx={formWrapper}>
        <FormControl>
          <Autocomplete
            value={address}
            // filterOptions={(option) => option}
            options={addresses}
            onChange={(e, value): void => handleClickAddress(value || '')}
            renderInput={(params) => (
              <TextField
                {...register('address', {
                  required,
                  validate: () => !clickAddress,
                })}
                value={address}
                error={errors?.address?.type === 'required' || errors?.address?.type === 'validate'}
                onChange={(e) => handleChangeAddress(e.target.value)}
                label="Address"
                sx={borderInput}
                {...params}
              />
            )}
          />
          <div style={{ position: 'absolute', width: '100%' }}>
            {errors?.address?.type === 'required' && <ErrorText text={errorNames.addressRequired} />}
            {errors?.address?.type === 'validate' && <ErrorText text={errorNames.addressValidate} />}
          </div>
        </FormControl>
        <FormControl>
          <TextField
            id="outlined-basic"
            label="Max amount of volunteers*"
            type="number"
            {...register('maximumVolunteers', {
              required,
              min: 1,
              maxLength: 3,
            })}
            error={!!errors?.maximumVolunteers}
            sx={borderInput}
          />
          {errors?.maximumVolunteers?.type === 'required' && <ErrorText text={errorNames.volunteersAmountRequired} />}
          {errors?.maximumVolunteers?.type === 'maxLength' && <ErrorText text={errorNames.volunteersAmountLength} />}
        </FormControl>
        <FormControl>
          <TextField
            id="outlined-basic"
            label="Contact name*"
            {...register('contactName', {
              required,
              minLength: 1,
              maxLength: 100,
            })}
            error={!!errors?.contactName}
            sx={borderInput}
          />
          {errors?.contactName?.type === 'required' && <ErrorText text={errorNames.contactNameRequired} />}
          {errors?.contactName?.type === 'maxLength' && <ErrorText text={errorNames.contactNameLength} />}
        </FormControl>
        <FormControl>
          <TextField
            id="outlined-basic"
            label="Contact email*"
            {...register('contactEmail', {
              required,
              pattern: errorRegExp.email,
            })}
            error={!!errors?.contactEmail}
            sx={borderInput}
          />
          {errors?.contactEmail?.type === 'required' && <ErrorText text={errorNames.emailRequired} />}
          {errors?.contactEmail?.type === 'pattern' && <ErrorText text={errorNames.invalidEmail} />}
        </FormControl>
      </Box>
      <FormControl>
        <PhoneInput
          control={control}
          sx={borderInput}
          id="outlined-basic"
          label="Phone number"
          fullWidth={true}
          require={false}
          {...register('contactPhone')}
          error={!!errors?.contactPhone}
        />
        {errors?.contactPhone?.type === 'pattern' && <ErrorText text={errorNames.invalidPhone} />}
      </FormControl>
    </Box>
  );
};
